import React, { useEffect, useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { ShoppingBasket } from 'lucide-react';
import { CartDrawer } from './CartDrawer';
import { CartFinalizePrompt } from './CartFinalizePrompt';
import {
  isCartRecentlyHighlighted,
  isCartFinalizePromptDismissed,
  shouldAutoOpenCartDrawer,
  shouldShowCartFinalizePrompt,
} from './cartFinalizeUx';
import { shouldOfferAnotherSlotSameService } from './cartAnotherSlotUx';
import { useBookingAnotherSlot } from '../../contexts/BookingAnotherSlotContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function CartManager() {
  const { t } = useTranslation('booking');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [dismissedAtMs, setDismissedAtMs] = useState<number | null>(null);
  const { itemCount, lastAddedAtMs, lastAddedItem } = useCart();
  const { requestAnotherSlot } = useBookingAnotherSlot();
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const checkoutOn = isFeatureEnabled(FeatureFlag.CHECKOUT_PAYMENTS);

  const showFinalizePrompt =
    shouldShowCartFinalizePrompt({
      checkoutEnabled: checkoutOn,
      itemCount,
      pathname: location.pathname,
    }) && !isCartFinalizePromptDismissed(dismissedAtMs, lastAddedAtMs);

  const highlighted = isCartRecentlyHighlighted(lastAddedAtMs);

  useEffect(() => {
    if (shouldAutoOpenCartDrawer(lastAddedAtMs, itemCount, isDrawerOpen) && showFinalizePrompt) {
      setIsDrawerOpen(true);
    }
  }, [lastAddedAtMs, itemCount, isDrawerOpen, showFinalizePrompt]);

  if (!checkoutOn || itemCount === 0 || location.pathname.includes('/checkout')) return null;
  if (location.pathname.startsWith('/embed/')) return null;

  return (
    <>
      <div className={`cart-fab-stack${showFinalizePrompt ? ' cart-fab-stack--with-prompt' : ''}`}>
        <AnimatePresence>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
          >
            <button
              onClick={() => setIsDrawerOpen(true)}
              className={`group relative flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 ${
                highlighted ? 'ring-4 ring-[#a5765c]/40 scale-105' : ''
              }`}
              style={{
                background: 'linear-gradient(135deg, #a5765c 0%, #8f6249 100%)',
                color: 'white',
              }}
              aria-label={t('cart.fabAria')}
            >
              <ShoppingBasket className='w-6 h-6' />

              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                key={itemCount}
                className='absolute -top-1 -right-1 flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1 ring-2 ring-white'
              >
                {itemCount}
              </motion.span>

              {highlighted ? (
                <div className='absolute inset-0 rounded-full animate-ping bg-[#a5765c] opacity-30 pointer-events-none' />
              ) : (
                <div className='absolute inset-0 rounded-full animate-ping bg-[#a5765c] opacity-20 pointer-events-none' />
              )}
            </button>
          </motion.div>
        </AnimatePresence>

        {showFinalizePrompt && (
          <CartFinalizePrompt
            stacked
            itemCount={itemCount}
            highlighted={highlighted}
            onViewCart={() => setIsDrawerOpen(true)}
            showAddAnotherSlot={
              shouldOfferAnotherSlotSameService(itemCount) &&
              lastAddedItem != null &&
              lastAddedItem.serviceId != null
            }
            onAddAnotherSlot={() => {
              if (lastAddedItem) requestAnotherSlot(lastAddedItem);
            }}
            onDismiss={() => setDismissedAtMs(lastAddedAtMs ?? Date.now())}
          />
        )}
      </div>

      <CartDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  );
}
